(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-product-comparison]';
  const SCRIPT_VERSION = '2026-10-28.1';
  const DEFAULT_STICKY_TOP = 0;
  const STICKY_NAV_CANDIDATE_SELECTOR = [
    'header',
    'nav',
    '[role="navigation"]',
    '[data-sticky]',
    '[data-sticky-navigation]',
    '[data-position-navigation]',
    '[class*="sticky" i]',
    '[class*="anchor" i]',
    '[class*="position" i]'
  ].join(',');

  if (window.BoyaProductComparison?.version === SCRIPT_VERSION) {
    window.BoyaProductComparison.scan();
    return;
  }

  try {
    window.BoyaProductComparison?.destroy?.(document);
  } catch (error) {
    console.warn('Product Comparison: unable to dispose a previous script instance.', error);
  }

  const instances = new WeakMap();
  const liveInstances = new Set();
  const pendingRoots = new WeakSet();
  const preloadedImages = new Set();
  const productPricingCache = new Map();

  const parseIndex = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : fallback;
  };

  const formatMetafield = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object' && 'value' in value) {
      return value.value === null || value.value === undefined || value.value === ''
        ? '-'
        : String(value.value);
    }
    return String(value);
  };

  const normalizeComparisonValue = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const localeFor = (language, country) => {
    const normalizedLanguage = String(language || 'en').replace(/_/g, '-');
    const normalizedCountry = String(country || '').trim().toUpperCase();

    try {
      if (typeof Intl.Locale === 'function') {
        return new Intl.Locale(
          normalizedLanguage,
          /^(?:[A-Z]{2}|\d{3})$/.test(normalizedCountry)
            ? { region: normalizedCountry }
            : {}
        ).toString();
      }
      return Intl.getCanonicalLocales(normalizedLanguage)[0] || 'en';
    } catch (error) {
      return 'en';
    }
  };

  const formatPrice = (value, context = {}) => {
    const cents = Number(value);
    if (!Number.isFinite(cents)) return '';

    const currency = String(context.currency || '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency)) return (cents / 100).toFixed(2);

    if (window.boyaProductMoney?.format) {
      const formatted = window.boyaProductMoney.format(cents, context);
      if (formatted) return formatted;
    }

    try {
      return new Intl.NumberFormat(localeFor(context.language, context.country), {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
        useGrouping: true
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };
  const displayPriceCache = new Map();

  const getDisplayPrice = (formattedValue, rawValue, context) => {
    const fallback = formatPrice(rawValue, context);
    if (formattedValue === null || formattedValue === undefined || formattedValue === '') return fallback;

    const source = String(formattedValue);
    if (displayPriceCache.has(source)) return displayPriceCache.get(source);

    const template = document.createElement('template');
    template.innerHTML = source;
    const displayValue = normalizeComparisonValue(template.content.textContent) || fallback;
    displayPriceCache.set(source, displayValue);
    return displayValue;
  };

  const parseDiscountNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const parsed = Number.parseFloat(String(value).replace(/,/g, '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const resolveDiscountConfiguration = (configuration) => {
    if (!configuration?.enabled || !configuration.code) return null;

    let type = String(configuration.type || 'auto').toLowerCase();
    let value = parseDiscountNumber(configuration.value);
    const sourceText = [
      configuration.discountText,
      configuration.savingsLabel,
      configuration.badgeLabel
    ].filter(Boolean).join(' ');

    if (type === 'auto' || !value) {
      const percentage = sourceText.match(/(\d+(?:\.\d+)?)\s*%/);
      if (percentage) {
        type = 'percent';
        value = Number.parseFloat(percentage[1]);
      } else {
        const fixed = sourceText.match(/(?:save|saving|off|coupon|code)?\s*[^0-9]*(\d+(?:\.\d+)?)/i);
        if (fixed) {
          type = 'fixed';
          value = Number.parseFloat(fixed[1]);
        }
      }
    }

    if (type === 'percentage') type = 'percent';
    if (type === 'fixed_amount') type = 'fixed';
    return value > 0 ? { type, value } : null;
  };

  const calculateDiscountAmount = (price, configuration) => {
    const resolved = resolveDiscountConfiguration(configuration);
    const cents = Number(price);
    if (!resolved || !Number.isFinite(cents) || cents <= 0) return 0;

    let amount = 0;
    if (resolved.type === 'percent') amount = Math.round(cents * resolved.value / 100);
    if (resolved.type === 'fixed') amount = Math.round(resolved.value * 100);
    return Math.max(0, Math.min(amount, cents));
  };

  const resolveProductPrice = (product, discountConfiguration) => {
    const basePrice = Number(product?.price || 0);
    const nativeCompareAt = Number(product?.compare_at_price || 0);
    const discountAmount = calculateDiscountAmount(basePrice, discountConfiguration);

    if (discountAmount > 0) {
      return {
        price: Math.max(0, basePrice - discountAmount),
        price_formatted: null,
        compare_at_price: basePrice,
        compare_at_price_formatted: product?.price_formatted || null,
        discount_applied: true
      };
    }

    return {
      price: basePrice,
      price_formatted: product?.price_formatted || null,
      compare_at_price: nativeCompareAt,
      compare_at_price_formatted: product?.compare_at_price_formatted || null,
      discount_applied: false
    };
  };

  const localizedProductUrl = (productUrl, localizationRoot) => {
    if (!productUrl) return null;

    try {
      const url = new URL(productUrl, window.location.origin);
      if (url.origin !== window.location.origin) return null;

      let rootPath = String(localizationRoot || window.Shopify?.routes?.root || '/').trim();
      if (!rootPath.startsWith('/')) rootPath = `/${rootPath}`;
      if (!rootPath.endsWith('/')) rootPath += '/';

      if (rootPath !== '/' && !url.pathname.startsWith(rootPath)) {
        const productsPathIndex = url.pathname.indexOf('/products/');
        if (productsPathIndex >= 0) {
          url.pathname = `${rootPath.replace(/\/$/, '')}${url.pathname.slice(productsPathIndex)}`;
        }
      }

      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      return url;
    } catch (error) {
      return null;
    }
  };

  const readDiscountConfiguration = (documentRoot) => {
    const blocks = Array.from(documentRoot.querySelectorAll('.dcc-block-root[data-dcc-block-id]'));
    const activeBlock = blocks.find((block) => (
      block.dataset.dccEnabled === 'true' && Boolean(block.dataset.dccCode)
    ));

    if (!activeBlock) return null;
    return {
      enabled: true,
      code: activeBlock.dataset.dccCode || '',
      type: activeBlock.dataset.dccDiscountType || 'auto',
      value: activeBlock.dataset.dccDiscountValue || '',
      discountText: activeBlock.dataset.dccDiscountText || '',
      savingsLabel: activeBlock.dataset.dccSavings || '',
      badgeLabel: activeBlock.dataset.dccBadge || ''
    };
  };

  const parseDiscountSection = (html) => {
    if (!html || String(html).trim() === 'null') return { valid: false, configuration: null };

    /* Product pages are large. Parse only Discount Code opening tags instead
       of building a second full-page DOM tree in memory. */
    const source = String(html);
    const openingTags = source.match(
      /<div\b[^>]*class=(?:"[^"]*\bdcc-block-root\b[^"]*"|'[^']*\bdcc-block-root\b[^']*')[^>]*>/gi
    ) || [];
    const template = document.createElement('template');
    template.innerHTML = openingTags.map((tag) => `${tag}</div>`).join('');

    return {
      valid: /id="MainProduct-|data-boya-product-money/.test(source),
      configuration: readDiscountConfiguration(template.content)
    };
  };

  const fetchDiscountConfiguration = (product, localizationRoot) => {
    const pageUrl = localizedProductUrl(product?.url, localizationRoot);
    if (!pageUrl) return Promise.resolve(null);

    const cacheKey = pageUrl.toString();
    if (productPricingCache.has(cacheKey)) return productPricingCache.get(cacheKey);

    const normalizePath = (path) => String(path || '/').replace(/\/+$/, '') || '/';
    const currentMainProduct = document.querySelector('[id^="MainProduct-"]');
    if (
      normalizePath(pageUrl.pathname) === normalizePath(window.location.pathname)
      && currentMainProduct
    ) {
      const currentPageConfiguration = Promise.resolve(readDiscountConfiguration(currentMainProduct));
      productPricingCache.set(cacheKey, currentPageConfiguration);
      return currentPageConfiguration;
    }

    const requestHtml = (url) => fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    });

    const request = requestHtml(pageUrl)
      .then((html) => parseDiscountSection(html).configuration)
      .catch(() => null);

    productPricingCache.set(cacheKey, request);
    return request;
  };

  const closeAll = (except = null) => {
    liveInstances.forEach((instance) => {
      if (instance !== except) instance.closeAllDropdowns();
    });
  };

  class ProductComparison {
    constructor(root) {
      this.root = root;
      this.productsData = this.readProducts();
      this.selects = Array.from(root.querySelectorAll('.comparison-select'));
      this.cards = Array.from(root.querySelectorAll('.comparison-product-card'));
      this.checkboxes = Array.from(root.querySelectorAll('[data-hide-duplicates]'));
      this.categories = Array.from(root.querySelectorAll('.comparison-category'));
      this.controls = root.querySelector('.comparison-controls');
      this.priceContext = {
        language: root.dataset.priceLanguage || document.documentElement.lang || 'en',
        country: root.dataset.priceCountry || '',
        currency: root.dataset.priceCurrency || window.Shopify?.currency?.active || ''
      };
      this.localizationRoot = root.dataset.localizationRoot || window.Shopify?.routes?.root || '/';
      this.mobileQuery = window.matchMedia?.('(max-width: 749px)') || null;
      this.stickyCandidateNodes = [];
      this.stickyOffsetFrame = 0;
      this.stickyRefreshTimer = 0;
      this.pricePreloadTimer = 0;
      this.rows = Array.from(root.querySelectorAll('.comparison-row')).map((element) => ({
        element,
        key: element.dataset.field,
        values: Array.from(element.querySelectorAll('.comparison-value'))
      }));
      this.selectedProducts = this.selects.map((select, index) => {
        const selected = select.querySelector('.custom-option[data-selected="true"]');
        return parseIndex(selected?.dataset.value, Math.min(index, this.productsData.length - 1));
      });

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      root.addEventListener('click', this.onClick);
      root.addEventListener('change', this.onChange);
      root.addEventListener('pointerover', this.onPointerOver, { passive: true });
      root.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      if (this.mobileQuery?.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onViewportChange);
      } else {
        this.mobileQuery?.addListener?.(this.onViewportChange);
      }
      this.refreshDuplicateRows();
      this.collectStickyCandidates();
      this.updateStickyOffset();
      this.stickyRefreshTimer = window.setTimeout(() => {
        this.collectStickyCandidates();
        this.scheduleStickyOffset();
      }, 1000);
      root.dataset.productComparisonReady = 'true';

      this.selectedProducts.forEach((productIndex, slot) => {
        this.refreshProductPricing(slot, productIndex);
      });
    }

    readProducts() {
      const dataElement = this.root.querySelector('[data-comparison-products]');
      if (!dataElement) return [];

      try {
        const products = JSON.parse(dataElement.textContent);
        return Array.isArray(products) ? products : [];
      } catch (error) {
        console.error('Product Comparison: invalid product data.', error);
        return [];
      }
    }

    onClick(event) {
      const option = event.target.closest('.custom-option');
      if (option && this.root.contains(option)) {
        const select = option.closest('.comparison-select');
        const slot = parseIndex(select?.dataset.productIndex);
        const productIndex = parseIndex(option.dataset.value, -1);

        if (select && this.productsData[productIndex]) {
          this.selectProduct(slot, productIndex, select);
          this.closeDropdown(select);
        }
        return;
      }

      const trigger = event.target.closest('.comparison-select-trigger');
      if (!trigger || !this.root.contains(trigger)) return;

      const select = trigger.closest('.comparison-select');
      if (!select) return;

      const shouldOpen = !select.classList.contains('active');
      closeAll(this);
      this.closeAllDropdowns();
      if (shouldOpen) this.openDropdown(select);
    }

    onChange(event) {
      if (!this.checkboxes.includes(event.target)) return;

      const shouldHide = event.target.checked;
      this.checkboxes.forEach((checkbox) => {
        checkbox.checked = shouldHide;
        checkbox.setAttribute('aria-checked', String(shouldHide));
      });
      this.refreshDuplicateRows(shouldHide);
    }

    onPointerOver(event) {
      const option = event.target.closest('.custom-option');
      if (!option || !this.root.contains(option)) return;
      const productIndex = parseIndex(option.dataset.value, -1);
      this.preloadProduct(productIndex);
      window.clearTimeout(this.pricePreloadTimer);
      this.pricePreloadTimer = window.setTimeout(() => {
        this.resolveProductPricing(productIndex);
      }, 120);
    }

    onKeyDown(event) {
      const interactive = event.target.closest('.comparison-select-trigger, .custom-option');
      if (!interactive || !this.root.contains(interactive)) return;

      if (event.key === 'Escape') {
        this.closeAllDropdowns();
        interactive.closest('.comparison-select')?.querySelector('.comparison-select-trigger')?.focus();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        interactive.click();
      }
    }

    onViewportChange() {
      this.refreshDuplicateRows();
      this.onResize();
    }

    onScroll() {
      this.scheduleStickyOffset();
    }

    onResize() {
      this.collectStickyCandidates();
      this.scheduleStickyOffset();
    }

    collectStickyCandidates() {
      this.stickyCandidateNodes = Array.from(document.querySelectorAll(STICKY_NAV_CANDIDATE_SELECTOR));
    }

    findStickyLayer(node) {
      let current = node;
      while (current && current !== document.body) {
        const position = window.getComputedStyle(current).position;
        if (position === 'fixed' || position === 'sticky') return current;
        current = current.parentElement;
      }
      return null;
    }

    scheduleStickyOffset() {
      if (!this.controls || this.stickyOffsetFrame) return;
      this.stickyOffsetFrame = window.requestAnimationFrame(() => {
        this.stickyOffsetFrame = 0;
        this.updateStickyOffset();
      });
    }

    updateStickyOffset() {
      if (!this.controls) return;

      const controlsRect = this.controls.getBoundingClientRect();
      const stickyLayers = new Set();
      this.stickyCandidateNodes.forEach((node) => {
        const layer = this.findStickyLayer(node);
        if (layer) stickyLayers.add(layer);
      });

      let obstructionBottom = 0;
      stickyLayers.forEach((layer) => {
        if (!layer.isConnected || this.root.contains(layer) || layer.contains(this.root)) return;

        const style = window.getComputedStyle(layer);
        const rect = layer.getBoundingClientRect();
        const isVisible = style.display !== 'none'
          && style.visibility !== 'hidden'
          && Number.parseFloat(style.opacity || '1') > 0;
        const overlapsHorizontally = rect.right > controlsRect.left && rect.left < controlsRect.right;
        const isTopNavigation = rect.bottom > 0
          && rect.top < Math.min(200, window.innerHeight * 0.25)
          && rect.height > 0
          && rect.height <= 200;

        if (isVisible && overlapsHorizontally && isTopNavigation) {
          obstructionBottom = Math.max(obstructionBottom, rect.bottom);
        }
      });

      const detectedTop = obstructionBottom > 0 ? Math.ceil(obstructionBottom + 8) : DEFAULT_STICKY_TOP;
      const maximumTop = Math.max(DEFAULT_STICKY_TOP, Math.floor(window.innerHeight * 0.35));
      const stickyTop = Math.min(Math.max(DEFAULT_STICKY_TOP, detectedTop), maximumTop);
      this.root.style.setProperty('--product-comparison-sticky-top', `${stickyTop}px`);
    }

    openDropdown(select) {
      const options = select.querySelector('.comparison-select-options');
      if (options) {
        options.hidden = false;
        options.setAttribute('aria-hidden', 'false');
      }
      select.classList.add('active');
      select.querySelector('.comparison-select-trigger')?.setAttribute('aria-expanded', 'true');
    }

    closeDropdown(select) {
      select.classList.remove('active');
      select.querySelector('.comparison-select-trigger')?.setAttribute('aria-expanded', 'false');
      const options = select.querySelector('.comparison-select-options');
      if (options) {
        options.hidden = true;
        options.setAttribute('aria-hidden', 'true');
      }
    }

    closeAllDropdowns() {
      this.selects.forEach((select) => this.closeDropdown(select));
    }

    selectProduct(slot, productIndex, select) {
      const product = this.productsData[productIndex];
      if (!product) return;

      this.preloadProduct(productIndex);
      this.selectedProducts[slot] = productIndex;
      this.updateOptionState(select, productIndex);
      this.updateTriggerDisplay(select, product);
      this.updateProductCard(this.cards[slot], product);
      this.updateTableColumn(slot, product);
      this.refreshProductPricing(slot, productIndex);
    }

    updateOptionState(select, productIndex) {
      const previous = select.querySelector('.custom-option[data-selected="true"]');
      const next = select.querySelector(`.custom-option[data-value="${productIndex}"]`);

      if (previous && previous !== next) {
        previous.dataset.selected = 'false';
        previous.setAttribute('aria-selected', 'false');
      }
      if (next) {
        next.dataset.selected = 'true';
        next.setAttribute('aria-selected', 'true');
      }
    }

    updateTriggerDisplay(select, product) {
      const display = select.querySelector('.selected-product-display');
      if (!display) return;

      let imageContainer = display.querySelector('.selected-product-image');
      if (!imageContainer) {
        imageContainer = document.createElement('div');
        imageContainer.className = 'selected-product-image';
        display.prepend(imageContainer);
      }

      let title = display.querySelector('.selected-product-title');
      if (!title) {
        title = document.createElement('span');
        title.className = 'selected-product-title';
        display.append(title);
      }

      this.updateImage(imageContainer, product.thumbnail || product.image, product, 50, 'low');
      title.textContent = product.title || '';
    }

    updateProductCard(card, product) {
      if (!card) return;

      const imageWrapper = card.querySelector('.product-image-wrapper');
      if (imageWrapper) this.updateImage(imageWrapper, product.image, product, 400, 'high');

      const priceElement = card.querySelector('.product-price');
      if (priceElement) this.updatePrice(priceElement, product);

      const buyButton = card.querySelector('.button-primary');
      const learnMoreLink = card.querySelector('.product-learn-more');
      if (buyButton) buyButton.href = product.url || '#';
      if (learnMoreLink) learnMoreLink.href = product.url || '#';
    }

    updateImage(container, source, product, width, priority) {
      let image = container.querySelector('img');

      if (!source) {
        if (image) image.hidden = true;
        return;
      }

      if (!image) {
        image = document.createElement('img');
        container.replaceChildren(image);
      }

      image.hidden = false;
      image.alt = product.image_alt || product.title || '';
      image.width = width;
      image.height = Math.max(1, Math.round(width / Number(product.image_aspect_ratio || 1)));
      image.decoding = 'async';
      image.loading = priority === 'high' ? 'eager' : 'lazy';
      image.fetchPriority = priority;
      if (image.getAttribute('src') !== source) image.src = source;
    }

    updatePrice(container, product) {
      const pricing = product.comparison_pricing || resolveProductPrice(product, null);
      const salePrice = document.createElement('span');
      salePrice.className = 'price-item';
      salePrice.textContent = getDisplayPrice(
        pricing.price_formatted,
        pricing.price,
        this.priceContext
      );

      if (pricing.compare_at_price && Number(pricing.compare_at_price) > Number(pricing.price)) {
        salePrice.classList.add('price-item--sale');
        const regularPrice = document.createElement('span');
        regularPrice.className = 'price-item price-item--regular';
        const strike = document.createElement('s');
        strike.textContent = getDisplayPrice(
          pricing.compare_at_price_formatted,
          pricing.compare_at_price,
          this.priceContext
        );
        regularPrice.append(strike);
        container.replaceChildren(salePrice, regularPrice);
        return;
      }

      container.replaceChildren(salePrice);
    }

    resolveProductPricing(productIndex) {
      const product = this.productsData[productIndex];
      if (!product) return Promise.resolve(null);
      if (product.comparison_pricing_resolved) return Promise.resolve(product);
      if (product.comparison_pricing_promise) return product.comparison_pricing_promise;

      product.comparison_pricing_promise = fetchDiscountConfiguration(product, this.localizationRoot)
        .then((configuration) => {
          product.comparison_pricing = resolveProductPrice(product, configuration);
          product.comparison_pricing_resolved = true;
          return product;
        });

      return product.comparison_pricing_promise;
    }

    refreshProductPricing(slot, productIndex) {
      this.resolveProductPricing(productIndex).then((product) => {
        if (!product || !this.root.isConnected || this.selectedProducts[slot] !== productIndex) return;
        const priceElement = this.cards[slot]?.querySelector('.product-price');
        if (priceElement) this.updatePrice(priceElement, product);
      });
    }

    updateTableColumn(slot, product) {
      this.rows.forEach((row) => {
        if (row.values[slot]) {
          row.values[slot].textContent = formatMetafield(product.metafields?.[row.key]);
        }
      });

      this.refreshDuplicateRows();
    }

    refreshDuplicateRows(shouldHide = this.checkboxes.some((checkbox) => checkbox.checked)) {
      const renderedColumnCount = Math.min(this.selects.length, this.cards.length, 3);
      const comparedColumnCount = this.mobileQuery?.matches
        ? Math.min(2, renderedColumnCount)
        : renderedColumnCount;

      this.rows.forEach((row) => {
        const values = row.values
          .slice(0, comparedColumnCount)
          .map((value) => normalizeComparisonValue(value.textContent));
        const isDuplicate = values.length > 1 && values.every((value) => value === values[0]);

        row.element.classList.toggle('row-duplicate', isDuplicate);
        row.element.classList.toggle('hidden', shouldHide && isDuplicate);
      });

      this.categories.forEach((category) => {
        const rows = Array.from(category.querySelectorAll('.comparison-row'));
        const hasVisibleRow = rows.some((row) => !row.classList.contains('hidden'));
        category.classList.toggle('comparison-category--empty', rows.length > 0 && !hasVisibleRow);
      });
    }

    preloadProduct(productIndex) {
      const source = this.productsData[productIndex]?.image;
      if (!source || preloadedImages.has(source)) return;

      preloadedImages.add(source);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'high';
      image.src = source;
    }

    destroy() {
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('pointerover', this.onPointerOver);
      this.root.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.stickyRefreshTimer);
      window.clearTimeout(this.pricePreloadTimer);
      if (this.stickyOffsetFrame) window.cancelAnimationFrame(this.stickyOffsetFrame);
      if (this.mobileQuery?.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onViewportChange);
      } else {
        this.mobileQuery?.removeListener?.(this.onViewportChange);
      }
      delete this.root.dataset.productComparisonReady;
    }
  }

  const isNearViewport = (root) => {
    const rect = root.getBoundingClientRect();
    return rect.bottom >= -800 && rect.top <= window.innerHeight + 800;
  };

  const initRoot = (root) => {
    if (!root || instances.has(root) || !root.querySelector('[data-comparison-products]')) return;

    pendingRoots.delete(root);
    observer?.unobserve(root);
    const instance = new ProductComparison(root);
    instances.set(root, instance);
    liveInstances.add(instance);
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) initRoot(entry.target);
        });
      }, { rootMargin: '800px 0px' })
    : null;

  const watchRoot = (root) => {
    if (instances.has(root) || pendingRoots.has(root)) return;

    if (window.Shopify?.designMode || !observer || isNearViewport(root)) {
      initRoot(root);
      return;
    }

    pendingRoots.add(root);
    observer.observe(root);
  };

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const scan = (scope = document) => {
    rootsWithin(scope).forEach(watchRoot);
  };

  const initFromInteraction = (event) => {
    const interactive = event.target.closest?.(`${ROOT_SELECTOR} .comparison-select-trigger`);
    if (!interactive) return;
    initRoot(interactive.closest(ROOT_SELECTOR));
  };

  const destroyWithin = (scope) => {
    rootsWithin(scope).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      liveInstances.delete(instance);
      instances.delete(root);
    });
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest(`${ROOT_SELECTOR} .comparison-select`)) closeAll();
  });
  document.addEventListener('pointerdown', initFromInteraction, { capture: true, passive: true });
  document.addEventListener('focusin', initFromInteraction, true);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  window.BoyaProductComparison = { version: SCRIPT_VERSION, scan, init: initRoot, destroy: destroyWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }
})();
